import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../LanguageContext';
import { presetRange, PRESET_KEYS, PRESET_LABEL_KEY, ksaDate } from '../utils/dateRange';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, Cell, LabelList,
} from 'recharts';

const tooltipStyle = { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, color: 'var(--text-primary)', boxShadow: 'var(--card-hover-shadow)' };
const STATUS_COLORS = { new: '#6366f1', contacted: '#3b82f6', interested: '#8b5cf6', site_visit_scheduled: '#f59e0b', site_visit_done: '#f97316', offer_sent: '#06b6d4', negotiating: '#ec4899', won: '#22c55e', lost: '#ef4444' };
const PIE_COLORS = ['#6366f1', '#3b82f6', '#8b5cf6', '#f59e0b', '#f97316', '#06b6d4', '#ec4899', '#22c55e', '#ef4444'];
const SOURCE_LABELS = {
  snapchat: { en: 'Snapchat', ar: '\u0633\u0646\u0627\u0628\u0634\u0627\u062A' },
  snapchat_aqar: { en: 'Snapchat - Aqar', ar: '\u0633\u0646\u0627\u0628\u0634\u0627\u062A - \u0639\u0642\u0627\u0631' },
  snapchat_site_visit: { en: 'Snapchat - Site Visit', ar: '\u0633\u0646\u0627\u0628\u0634\u0627\u062A - \u0632\u0627\u0631 \u0627\u0644\u0645\u0634\u0631\u0648\u0639' },
  tiktok: { en: 'TikTok', ar: '\u062A\u064A\u0643\u062A\u0648\u0643' },
  google_ads: { en: 'Google Ads', ar: '\u0625\u0639\u0644\u0627\u0646\u0627\u062A \u062C\u0648\u062C\u0644' },
  instagram: { en: 'Instagram', ar: '\u0627\u0646\u0633\u062A\u063A\u0631\u0627\u0645' },
  facebook: { en: 'Facebook', ar: '\u0641\u064A\u0633\u0628\u0648\u0643' },
  aqar: { en: 'Aqar', ar: '\u0639\u0642\u0627\u0631' },
  site_visitor: { en: 'Site Visitor', ar: '\u0632\u0627\u0631 \u0627\u0644\u0645\u0634\u0631\u0648\u0639' },
  whatsapp: { en: 'WhatsApp', ar: '\u0648\u0627\u062a\u0633\u0627\u0628' },
  website: { en: 'Website', ar: '\u0627\u0644\u0645\u0648\u0642\u0639' },
};
const sourceLabel = (s, lang) => SOURCE_LABELS[s]?.[lang] || SOURCE_LABELS[s]?.en || s.replace('_', ' ');

// ── Lead Detail Panel (inline expand) ──
function LeadDetailPanel({ leadId, t }) {
  const [lead, setLead] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [leadRes, histRes] = await Promise.all([
          axios.get(`/api/leads/${leadId}`),
          axios.get(`/api/leads/${leadId}/transitions`),
        ]);
        setLead(leadRes.data.lead || leadRes.data);
        setHistory(histRes.data || []);
      } catch {}
      setLoading(false);
    })();
  }, [leadId]);

  if (loading) return <div style={{ padding: 12, color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{t('loading')}</div>;
  if (!lead) return <div style={{ padding: 12, color: 'var(--text-secondary)', fontSize: '0.8rem' }}>—</div>;

  // Strip the machine-readable transition meta (<!--meta:{...}-->) from each
  // message and drop entries that were nothing but meta — those details already
  // show cleanly in the status-history timeline below.
  const comms = (lead.communications || [])
    .map((c) => ({ ...c, text: (c.message || '').replace(/\n?<!--meta:[\s\S]*?-->/g, '').trim() }))
    .filter((c) => c.text)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div style={{ padding: '12px 16px', background: 'var(--bg-primary)', borderRadius: 10, margin: '4px 16px 8px', border: '1px solid var(--border)' }}>
      {/* Lead info grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: '0.78rem', marginBottom: 12 }}>
        <div><span style={{ color: 'var(--text-secondary)' }}>{t('crmLeadName')}:</span> <strong>{lead.name}</strong></div>
        <div><span style={{ color: 'var(--text-secondary)' }}>{t('crmPhone')}:</span> <strong style={{ direction: 'ltr', display: 'inline-block' }}>{lead.phone}</strong></div>
        {lead.email && <div><span style={{ color: 'var(--text-secondary)' }}>{t('crmEmail')}:</span> <strong style={{ direction: 'ltr', display: 'inline-block' }}>{lead.email}</strong></div>}
        <div><span style={{ color: 'var(--text-secondary)' }}>{t('crmSource')}:</span> <strong>{lead.source}</strong></div>
        <div><span style={{ color: 'var(--text-secondary)' }}>{t('crmStatusLabel')}:</span> <span style={{ padding: '1px 7px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 600, background: (STATUS_COLORS[lead.status] || '#888') + '18', color: STATUS_COLORS[lead.status] || '#888' }}>{t('crmStatus_' + lead.status)}</span></div>
        {lead.assignedTo && <div><span style={{ color: 'var(--text-secondary)' }}>{t('crmAssignedTo')}:</span> <strong>{lead.assignedTo}</strong></div>}
        {lead.project && <div><span style={{ color: 'var(--text-secondary)' }}>{t('crmProject')}:</span> <strong>{lead.project}</strong></div>}
        {lead.budget > 0 && <div><span style={{ color: 'var(--text-secondary)' }}>{t('crmBudget')}:</span> <strong>{lead.budget}</strong></div>}
        {lead.notes && (
          <div style={{ gridColumn: '1 / -1' }}>
            <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{t('crmNotes')}:</span>
            <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {lead.notes.split('\n').filter(Boolean).map((line, i) => {
                const match = line.match(/^\[(\d{4}-\d{2}-\d{2}(?:T[^\]]*)?)\]\s*(.*)/);
                if (match) {
                  const d = new Date(match[1]);
                  const dateStr = d.toLocaleDateString('en-GB', { timeZone: 'Asia/Riyadh', day: '2-digit', month: '2-digit', year: '2-digit' });
                  return <div key={i} style={{ padding: '3px 0', borderBottom: '1px solid var(--border)' }}><span style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', marginInlineEnd: 6 }}>{dateStr}</span>{match[2]}</div>;
                }
                return <div key={i} style={{ padding: '3px 0' }}>{line}</div>;
              })}
            </div>
          </div>
        )}
      </div>

      {/* Communication log — the actual conversation entries from crm_communications */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: '0.78rem', marginBottom: 8, color: 'var(--text-secondary)' }}>{t('crmCommunicationLog')}</div>
        {comms.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {comms.map((c, i) => (
              <div key={c.id || i} style={{ padding: '5px 8px', background: 'var(--bg-secondary)', borderRadius: 6, fontSize: '0.74rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  {c.type && <span style={{ padding: '0 6px', borderRadius: 8, fontSize: '0.62rem', fontWeight: 700, background: 'rgba(99,102,241,0.12)', color: 'var(--accent)' }}>{c.type}</span>}
                  {c.by && <span style={{ fontSize: '0.66rem', color: 'var(--accent)', fontWeight: 600 }}>{c.by}</span>}
                  <span style={{ marginInlineStart: 'auto', fontSize: '0.64rem', color: 'var(--text-secondary)' }}>
                    {c.date ? new Date(c.date).toLocaleString('en-GB', { timeZone: 'Asia/Riyadh', day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }) : ''}
                  </span>
                </div>
                <div style={{ color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{c.text}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>{t('crmNoComms')}</div>
        )}
      </div>

      {/* Status history timeline */}
      {history.length > 0 && (
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.78rem', marginBottom: 8, color: 'var(--text-secondary)' }}>{t('crmStatusHistory')}</div>
          <div style={{ borderLeft: '2px solid var(--border)', paddingLeft: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {history.map((h, i) => {
              let hData = {};
              try { hData = typeof h.data === 'string' ? JSON.parse(h.data) : (h.data || {}); } catch {}
              const note = hData.statusNote || '';
              return (
                <div key={i} style={{ fontSize: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLORS[h.to_status] || '#888', flexShrink: 0, marginLeft: -16 }} />
                    <span style={{ padding: '1px 6px', borderRadius: 8, fontSize: '0.68rem', fontWeight: 600, background: (STATUS_COLORS[h.to_status] || '#888') + '18', color: STATUS_COLORS[h.to_status] || '#888' }}>{t('crmStatus_' + h.to_status)}</span>
                    {h.done_by && <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{h.done_by}</span>}
                    <span style={{ color: 'var(--text-secondary)', marginLeft: 'auto' }}>{new Date(h.created_at).toLocaleString([], { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  {(note || hData.lossReason || hData.lossDetails || hData.project || hData.followUpDate) && (
                    <div style={{ marginLeft: 8, marginTop: 3, padding: '4px 8px', background: 'rgba(99,102,241,0.05)', borderRadius: 6, fontSize: '0.72rem', color: 'var(--text-primary)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {note && <div style={{ fontStyle: 'italic' }}>"{note}"</div>}
                      {hData.lossReason && <div><span style={{ color: 'var(--text-secondary)' }}>{t('crmLossReason')}:</span> <strong style={{ color: '#ef4444' }}>{hData.lossReason}</strong></div>}
                      {hData.lossDetails && <div><span style={{ color: 'var(--text-secondary)' }}>{t('wizLossDetails')}:</span> {hData.lossDetails}</div>}
                      {hData.project && <div><span style={{ color: 'var(--text-secondary)' }}>{t('crmProject')}:</span> {hData.project}</div>}
                      {hData.followUpDate && <div><span style={{ color: 'var(--text-secondary)' }}>{t('wizFollowUpDate')}:</span> {new Date(hData.followUpDate).toLocaleString()}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Member Tasks Detail Modal ──
// ── Team Today Board — outstanding work queue across all reps ──
function TodayBoard({ t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/api/leads/today-board');
        setData(res.data);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const fmtLate = d => {
    const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
    return (days < 1 ? 1 : days) + 'd';
  };
  const fmtAge = d => {
    const h = Math.floor((Date.now() - new Date(d).getTime()) / 3600000);
    return h < 24 ? `${h}h` : `${Math.floor(h / 24)}d`;
  };
  const fmtTime = d => new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const row = (item, meta) => {
    const isExp = expandedId === item.id;
    return (
      <div key={item.id}>
        <div onClick={() => setExpandedId(isExp ? null : item.id)} style={{
          padding: '9px 14px', borderBottom: '1px solid var(--border)', fontSize: '0.82rem',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          cursor: 'pointer', background: isExp ? 'rgba(99,102,241,0.04)' : 'transparent',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: STATUS_COLORS[item.status] || '#888' }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.7rem', color: 'var(--accent)', fontWeight: 600 }}>{item.assigned_to}</span>
                <span style={{ padding: '1px 7px', borderRadius: 10, fontSize: '0.66rem', fontWeight: 600, background: (STATUS_COLORS[item.status] || '#888') + '18', color: STATUS_COLORS[item.status] || '#888' }}>{t('crmStatus_' + item.status)}</span>
                {item.phone && <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', direction: 'ltr' }}>{item.phone}</span>}
              </div>
            </div>
          </div>
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: meta.color, flexShrink: 0, marginInlineStart: 8 }}>{meta.text}</span>
        </div>
        {isExp && <LeadDetailPanel leadId={item.id} t={t} />}
      </div>
    );
  };

  const sections = data ? [
    { key: 'overdue', label: t('crmTodayOverdue'), color: '#ef4444', items: data.overdue || [], meta: it => ({ text: fmtLate(it.follow_up_date), color: '#ef4444' }) },
    { key: 'uncontacted', label: t('crmTodayBehind'), color: '#f59e0b', items: data.uncontacted || [], meta: it => ({ text: fmtAge(it.created_at), color: '#f59e0b' }) },
    { key: 'visits', label: t('crmTodayVisits'), color: '#06b6d4', items: data.visits || [], meta: it => ({ text: fmtTime(it.site_visit_date), color: '#06b6d4' }) },
  ].filter(s => s.items.length > 0) : [];

  const doneSection = data ? {
    key: 'done', label: t('anCompleted'), color: '#22c55e', items: data.doneLeads || [],
    meta: it => ({ text: fmtTime(it.today_at), color: '#22c55e' }),
  } : null;

  const totalLeft = sections.reduce((a, s) => a + s.items.length, 0);
  const activeSection = active === 'done' ? doneSection : sections.find(s => s.key === active);

  return (
    <div className="card mb-6" style={{ overflow: 'visible' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: 16 }}>
        <h2 style={{ fontSize: '1rem', margin: 0, marginInlineEnd: 4 }}>{t('anTodayBoard')}</h2>
        {data && data.done > 0 && (() => {
          const on = active === 'done';
          return (
            <button onClick={() => setActive(on ? null : 'done')} style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 12px', borderRadius: 10,
              border: `1px solid ${on ? '#22c55e' : '#22c55e40'}`, cursor: 'pointer',
              background: on ? '#22c55e1a' : '#22c55e0c', color: '#22c55e', fontWeight: 700, fontSize: '0.78rem',
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} />
              {t('anCompleted')}
              <span style={{ fontWeight: 800 }}>{data.done}</span>
            </button>
          );
        })()}
        {loading ? (
          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{t('loading')}</span>
        ) : totalLeft === 0 ? (
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#22c55e' }}>{t('anAllDone')}</span>
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginInlineStart: 'auto' }}>
            {sections.map(s => {
              const on = active === s.key;
              return (
                <button key={s.key} onClick={() => setActive(on ? null : s.key)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 12px', borderRadius: 10,
                  border: `1px solid ${on ? s.color : s.color + '40'}`, cursor: 'pointer',
                  background: on ? s.color + '1a' : s.color + '0c', color: s.color, fontWeight: 700, fontSize: '0.78rem',
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color }} />
                  {s.label}
                  <span style={{ fontWeight: 800 }}>{s.items.length}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {activeSection && (
        <div style={{ borderTop: '1px solid var(--border)', maxHeight: '50vh', overflowY: 'auto' }}>
          {activeSection.items.map(it => row(it, activeSection.meta(it)))}
        </div>
      )}
    </div>
  );
}

function TouchLeadsModal({ name, bucket, endpoint, title, subtitle, onClose, t }) {
  const [leads, setLeads] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedLeadId, setExpandedLeadId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = endpoint
          ? await axios.get(endpoint)
          : await axios.get(`/api/leads/member-touch-leads/${encodeURIComponent(name)}`, { params: { bucket } });
        setLeads(res.data.leads || []);
      } catch {}
      setLoading(false);
    })();
  }, [name, bucket, endpoint]);

  const bucketLabel = bucket >= 5 ? t('anTouchN5') : bucket === 4 ? t('anTouchN4') : bucket === 3 ? t('anTouchN3') : bucket === 2 ? t('anTouchN2') : t('anTouchN1');

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div className="crm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="crm-modal-header" style={{ borderBottom: '1px solid var(--border)', paddingBottom: 12 }}>
          <div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>{title || name}</h2>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{subtitle || bucketLabel}</span>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>&times;</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 50, color: 'var(--text-secondary)' }}>{t('loading')}</div>
        ) : !leads || leads.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{t('noData')}</div>
        ) : (
          <>
          {(() => {
            const counts = {};
            leads.forEach(l => { counts[l.status] = (counts[l.status] || 0) + 1; });
            const order = ['won', 'negotiating', 'offer_sent', 'site_visit_done', 'site_visit_scheduled', 'interested', 'contacted', 'new', 'lost'];
            const chips = order.filter(s => counts[s]).map(s => ({ status: s, n: counts[s] }));
            return (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
                {chips.map(c => (
                  <span key={c.status} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 10, fontSize: '0.73rem', fontWeight: 700, background: (STATUS_COLORS[c.status] || '#888') + '18', color: STATUS_COLORS[c.status] || '#888' }}>
                    {t('crmStatus_' + c.status)}<b style={{ fontWeight: 800 }}>{c.n}</b>
                  </span>
                ))}
              </div>
            );
          })()}
          <div style={{ maxHeight: '56vh', overflowY: 'auto', padding: '12px 0' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {leads.map((item, i) => {
                const isExpanded = expandedLeadId === item.id;
                return (
                  <div key={i}>
                    <div onClick={() => setExpandedLeadId(isExpanded ? null : item.id)} style={{
                      padding: '10px 16px', borderBottom: '1px solid var(--border)', fontSize: '0.82rem',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      cursor: 'pointer', background: isExpanded ? 'rgba(99,102,241,0.04)' : 'transparent',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                        <span style={{
                          width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                          background: STATUS_COLORS[item.status] || '#888',
                        }} />
                        <div>
                          <div style={{ fontWeight: 600, marginBottom: 2 }}>{item.name}</div>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{
                              padding: '1px 7px', borderRadius: 10, fontSize: '0.68rem', fontWeight: 600,
                              background: (STATUS_COLORS[item.status] || '#888') + '18',
                              color: STATUS_COLORS[item.status] || '#888',
                            }}>{t('crmStatus_' + item.status)}</span>
                            {item.phone && <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', direction: 'ltr' }}>{item.phone}</span>}
                          </div>
                        </div>
                      </div>
                      {item.touches > 0 && <span title={t('crmTouchesHint')} style={{ padding: '1px 7px', borderRadius: 10, fontSize: '0.68rem', fontWeight: 700, flexShrink: 0, background: item.touches >= 3 ? '#22c55e18' : item.touches === 2 ? '#f59e0b18' : '#ef444418', color: item.touches >= 3 ? '#22c55e' : item.touches === 2 ? '#f59e0b' : '#ef4444' }}>{'\uD83D\uDCDE '}{item.touches}{'\u00D7'}</span>}
                    </div>
                    {isExpanded && <LeadDetailPanel leadId={item.id} t={t} />}
                  </div>
                );
              })}
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── KPI Detail Modal ──
function KpiDetailModal({ type, title, color, period, customFrom, customTo, member, status, onClose, t }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLeadId, setExpandedLeadId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { from, to } = presetRange(period, customFrom, customTo);
        const params = (from && to) ? { date_from: from, date_to: to } : { period: period || 'today' };
        if (member) params.member = member;
        if (status) params.status = status;
        const res = await axios.get(`/api/leads/kpi-detail/${type}`, { params });
        setLeads(res.data.leads || []);
      } catch {}
      setLoading(false);
    })();
  }, [type, period, customFrom, customTo, member, status]);

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div className="crm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="crm-modal-header" style={{ borderBottom: '1px solid var(--border)', paddingBottom: 12 }}>
          <div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0, color }}>{title}</h2>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{leads.length} {t('crmTotalLeads')}</span>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>&times;</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 50, color: 'var(--text-secondary)' }}>{t('loading')}</div>
        ) : leads.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 50, color: 'var(--text-secondary)' }}>{t('noData')}</div>
        ) : (
          <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
            {leads.map((lead, i) => {
              const leadId = lead.id || lead.lead_id;
              const isExpanded = expandedLeadId === leadId;
              return (
                <div key={i}>
                  <div onClick={() => setExpandedLeadId(isExpanded ? null : leadId)} style={{
                    padding: '10px 16px', borderBottom: '1px solid var(--border)', fontSize: '0.82rem',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    cursor: 'pointer', background: isExpanded ? 'rgba(99,102,241,0.04)' : 'transparent',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                        background: STATUS_COLORS[lead.to_status || lead.status] || '#888',
                      }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, marginBottom: 2 }}>{lead.name}</div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{
                            padding: '1px 7px', borderRadius: 10, fontSize: '0.68rem', fontWeight: 600,
                            background: (STATUS_COLORS[lead.to_status || lead.status] || '#888') + '18',
                            color: STATUS_COLORS[lead.to_status || lead.status] || '#888',
                          }}>{t('crmStatus_' + (lead.to_status || lead.status))}</span>
                          {lead.done_by && <span style={{ fontSize: '0.72rem', color: 'var(--accent)', fontWeight: 600 }}>{lead.done_by}</span>}
                          {lead.assigned_to && !lead.done_by && <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{lead.assigned_to}</span>}
                          {lead.phone && <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', direction: 'ltr' }}>{lead.phone}</span>}
                          {lead.loss_reason && <span style={{ fontSize: '0.72rem', color: '#ef4444' }}>{lead.loss_reason}</span>}
                        </div>
                        {lead.loss_details && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2, fontStyle: 'italic' }}>"{lead.loss_details}"</div>
                        )}
                      </div>
                    </div>
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', flexShrink: 0 }}>
                      {lead.updated_at ? new Date(lead.updated_at).toLocaleString([], { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                  {isExpanded && leadId && <LeadDetailPanel leadId={leadId} t={t} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Dashboard ──
// Paginated list of leads for one loss reason (optionally scoped to a source),
// each expandable to its full conversation + notes.
function LossDrill({ reason, source, period, customFrom, customTo, t }) {
  const LIMIT = 20;
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ leads: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [openLead, setOpenLead] = useState(null);
  useEffect(() => { setPage(0); }, [reason, source]);
  useEffect(() => {
    const { from, to } = presetRange(period, customFrom, customTo);
    const params = (from && to) ? { date_from: from, date_to: to } : { period: period || 'today' };
    params.reason = reason; params.limit = LIMIT; params.page = page; if (source) params.source = source;
    setLoading(true);
    axios.get('/api/leads/loss-reason-leads', { params }).then(r => setData(r.data || { leads: [], total: 0 })).catch(() => setData({ leads: [], total: 0 })).finally(() => setLoading(false));
  }, [reason, source, page, period, customFrom, customTo]);
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / LIMIT));
  return (
    <div style={{ marginTop: 6 }}>
      {loading ? (
        <div style={{ padding: 12, color: 'var(--text-secondary)', fontSize: '0.78rem' }}>{t('loading')}</div>
      ) : data.leads.map((lead, i) => (
        <div key={lead.id || i} style={{ borderBottom: '1px solid var(--border)' }}>
          <div onClick={() => setOpenLead(openLead === lead.id ? null : lead.id)} style={{ padding: '7px 2px', cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>{lead.name}</span>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{lead.assignedTo} {openLead === lead.id ? '▲' : '▼'}</span>
            </div>
            {lead.details && <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 2 }}>{lead.details}</div>}
          </div>
          {openLead === lead.id && lead.id && <LeadDetailPanel leadId={lead.id} t={t} />}
        </div>
      ))}
      {data.total > LIMIT && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '8px 0' }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: page === 0 ? 'default' : 'pointer', fontSize: '0.75rem', fontWeight: 700, color: page === 0 ? 'var(--border)' : 'var(--text-primary)' }}>‹</button>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{page + 1} / {totalPages} · {data.total}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: page >= totalPages - 1 ? 'default' : 'pointer', fontSize: '0.75rem', fontWeight: 700, color: page >= totalPages - 1 ? 'var(--border)' : 'var(--text-primary)' }}>›</button>
        </div>
      )}
    </div>
  );
}

function LossReasonsCard({ lossReasonsSummary, lossBySource, period, customFrom, customTo, t, lang }) {
  const [view, setView] = useState('reason'); // 'reason' | 'source'
  const [drill, setDrill] = useState(null);    // { reason, source }
  const [openSource, setOpenSource] = useState(null);
  const entries = Object.entries(lossReasonsSummary || {}).sort((a, b) => b[1].count - a[1].count);
  const total = entries.reduce((s, [, v]) => s + v.count, 0);
  if (entries.length === 0) return null;

  const tabBtn = (v, label) => (
    <button onClick={() => { setView(v); setDrill(null); setOpenSource(null); }}
      style={{ padding: '4px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', background: view === v ? 'var(--accent)' : 'var(--bg-primary)', color: view === v ? '#fff' : 'var(--text-secondary)' }}>{label}</button>
  );
  const bar = (label, count, pct, active, onClick) => (
    <div onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', background: active ? 'rgba(239,68,68,0.05)' : 'transparent', border: active ? '1px solid rgba(239,68,68,0.15)' : '1px solid transparent' }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{label}</span>
          <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#ef4444' }}>{count}</span>
        </div>
        <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}><div style={{ height: '100%', width: pct + '%', borderRadius: 2, background: '#ef4444' }} /></div>
      </div>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', width: 36, textAlign: 'right' }}>{pct.toFixed(0)}%</span>
    </div>
  );

  return (
    <div className="card mb-6">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h2>{t('anLossReasons')}</h2>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {tabBtn('reason', lang === 'ar' ? 'حسب السبب' : 'By reason')}
          {tabBtn('source', lang === 'ar' ? 'حسب المصدر' : 'By source')}
          <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{total}</span>
        </div>
      </div>
      <div style={{ padding: '8px 16px 16px' }}>
        {view === 'reason' ? entries.map(([reason, info]) => {
          const pct = total > 0 ? (info.count / total * 100) : 0;
          const active = drill && !drill.source && drill.reason === reason;
          return (
            <div key={reason} style={{ marginBottom: 8 }}>
              {bar(reason, info.count, pct, active, () => setDrill(active ? null : { reason, source: '' }))}
              {active && <div style={{ marginInlineStart: 12, borderInlineStart: '2px solid rgba(239,68,68,0.2)', paddingInlineStart: 12 }}>
                <LossDrill reason={reason} source="" period={period} customFrom={customFrom} customTo={customTo} t={t} />
              </div>}
            </div>
          );
        }) : (lossBySource || []).map(sg => {
          const isOpen = openSource === sg.source;
          return (
            <div key={sg.source} style={{ marginBottom: 8 }}>
              <div onClick={() => { setOpenSource(isOpen ? null : sg.source); setDrill(null); }} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: 10, cursor: 'pointer', background: isOpen ? 'var(--bg-secondary)' : 'transparent' }}>
                <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{sourceLabel(sg.source, lang)} <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem' }}>{isOpen ? '▲' : '▼'}</span></span>
                <span style={{ fontWeight: 700, color: '#ef4444' }}>{sg.total}</span>
              </div>
              {isOpen && <div style={{ marginInlineStart: 12, borderInlineStart: '2px solid var(--border)', paddingInlineStart: 12 }}>
                {sg.reasons.map((r, ri) => {
                  const pct = sg.total > 0 ? (r.count / sg.total * 100) : 0;
                  const active = drill && drill.source === sg.source && drill.reason === r.reason;
                  return (
                    <div key={ri}>
                      {bar(r.reason, r.count, pct, active, () => setDrill(active ? null : { reason: r.reason, source: sg.source }))}
                      {active && <LossDrill reason={r.reason} source={sg.source} period={period} customFrom={customFrom} customTo={customTo} t={t} />}
                    </div>
                  );
                })}
              </div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Status-transition explorer: "who moved from X → Y" (e.g. interested→lost,
// anything after a visit). Answers drop-off questions the funnel can't. ──
const TRANS_STATUSES = ['new', 'contacted', 'interested', 'site_visit_scheduled', 'site_visit_done', 'offer_sent', 'negotiating', 'won', 'lost'];
function TransitionExplorer({ period, customFrom, customTo, reps, t, lang }) {
  const [from, setFrom] = useState('');   // '' = any source status
  const [to, setTo] = useState('lost');
  const [member, setMember] = useState('');
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const PAGE_SIZE = 25;
  const arrow = lang === 'ar' ? '←' : '→';   // RTL reads right→left, so flip the glyph
  const fmt = d => d ? new Date(d).toLocaleString('en-GB', { timeZone: 'Asia/Riyadh', day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }) : '';

  // Reset to the first page whenever the filters change.
  useEffect(() => { setPage(0); }, [from, to, member, period, customFrom, customTo]);

  useEffect(() => {
    const { from: df, to: dt } = presetRange(period, customFrom, customTo);
    const params = (df && dt) ? { date_from: df, date_to: dt } : { period: period || 'today' };
    params.to = to; if (from) params.from = from; if (member) params.member = member;
    params.page = page; params.limit = PAGE_SIZE;
    setLoading(true); setOpenId(null);
    axios.get('/api/leads/transition-drill', { params })
      .then(r => { setLeads(r.data?.leads || []); setTotal(r.data?.total || 0); })
      .catch(() => { setLeads([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [from, to, member, period, customFrom, customTo, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const quick = [
    { label: `${t('crmStatus_site_visit_scheduled')} ${arrow} ${t('crmStatus_lost')}`, from: 'site_visit_scheduled,site_visit_done', to: 'lost' },
    { label: `${t('crmStatus_interested')} ${arrow} ${t('crmStatus_lost')}`, from: 'interested', to: 'lost' },
    { label: `${lang === 'ar' ? 'أي حالة' : 'Any'} ${arrow} ${t('crmStatus_lost')}`, from: '', to: 'lost' },
    { label: `${t('crmStatus_site_visit_scheduled')} ${arrow} ${t('crmStatus_won')}`, from: 'site_visit_scheduled,site_visit_done', to: 'won' },
  ];
  const sel = { padding: '6px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.78rem', cursor: 'pointer' };

  return (
    <div className="card" style={{ padding: 18, marginBottom: 16 }}>
      <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: '0 0 4px' }}>{lang === 'ar' ? 'مستكشف تحوّلات الحالة' : 'Status Transition Explorer'}</h3>
      <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginBottom: 10 }}>
        {lang === 'ar' ? 'العملاء الذين انتقلوا من حالة إلى أخرى خلال الفترة المحددة — اضغط أي عميل لعرض المحادثات والملاحظات' : 'Leads that moved from one status to another in the period — tap any lead to see its conversation and notes'}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {quick.map((qk, i) => (
          <button key={i} onClick={() => { setFrom(qk.from); setTo(qk.to); }}
            style={{ padding: '4px 10px', borderRadius: 14, border: '1px solid var(--border)', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', background: (from === qk.from && to === qk.to) ? 'var(--accent)' : 'var(--bg-primary)', color: (from === qk.from && to === qk.to) ? '#fff' : 'var(--text-secondary)' }}>{qk.label}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <select value={from} onChange={e => setFrom(e.target.value)} style={sel}>
          <option value="">{lang === 'ar' ? 'من: أي حالة' : 'From: any status'}</option>
          {TRANS_STATUSES.map(st => <option key={st} value={st}>{t('crmStatus_' + st)}</option>)}
        </select>
        <span style={{ color: 'var(--text-secondary)' }}>{arrow}</span>
        <select value={to} onChange={e => setTo(e.target.value)} style={sel}>
          {TRANS_STATUSES.map(st => <option key={st} value={st}>{t('crmStatus_' + st)}</option>)}
        </select>
        <select value={member} onChange={e => setMember(e.target.value)} style={sel}>
          <option value="">{lang === 'ar' ? 'كل موظفي المبيعات' : 'All reps'}</option>
          {(reps || []).map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 700 }}>{loading ? '…' : `${total}`}</span>
      </div>
      <div style={{ maxHeight: 420, overflowY: 'auto', border: leads.length ? '1px solid var(--border)' : 'none', borderRadius: 10 }}>
        {loading ? null : leads.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{t('noData')}</div>
        ) : leads.map((l, i) => {
          const fc = STATUS_COLORS[l.from_status] || '#888', tc = STATUS_COLORS[l.to_status] || '#888';
          const key = (l.id || '') + '_' + i;
          const isOpen = openId === key;
          return (
            <div key={key} style={{ borderBottom: i < leads.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <div onClick={() => l.id && setOpenId(isOpen ? null : key)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', fontSize: '0.78rem', flexWrap: 'wrap', cursor: l.id ? 'pointer' : 'default', background: isOpen ? 'var(--bg-secondary)' : 'transparent' }}>
                <b style={{ minWidth: 90 }}>{l.name}</b>
                <a href={`tel:${l.phone}`} onClick={e => e.stopPropagation()} style={{ direction: 'ltr', color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '0.72rem' }}>{l.phone}</a>
                <span style={{ padding: '1px 6px', borderRadius: 6, fontSize: '0.6rem', fontWeight: 700, background: fc + '18', color: fc }}>{t('crmStatus_' + l.from_status)}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{arrow}</span>
                <span style={{ padding: '1px 6px', borderRadius: 6, fontSize: '0.6rem', fontWeight: 700, background: tc + '18', color: tc }}>{t('crmStatus_' + l.to_status)}</span>
                {l.lossReason && <span style={{ fontSize: '0.66rem', color: '#ef4444' }}>{t('crmLoss_' + l.lossReason) || l.lossReason}</span>}
                <span style={{ marginInlineStart: 'auto', fontSize: '0.66rem', color: 'var(--text-secondary)' }}>{l.assigned_to} · {fmt(l.when)}</span>
                {l.id && <span style={{ color: tc, fontSize: '0.7rem' }}>{isOpen ? '▲' : '▼'}</span>}
              </div>
              {isOpen && l.id && <LeadDetailPanel leadId={l.id} t={t} />}
            </div>
          );
        })}
      </div>
      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 10 }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0 || loading}
            style={{ padding: '4px 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.75rem', fontWeight: 600, cursor: page === 0 ? 'default' : 'pointer', opacity: page === 0 ? 0.4 : 1 }}>
            {lang === 'ar' ? 'السابق' : 'Prev'}
          </button>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
            {lang === 'ar' ? `صفحة ${page + 1} من ${totalPages}` : `Page ${page + 1} of ${totalPages}`}
          </span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1 || loading}
            style={{ padding: '4px 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.75rem', fontWeight: 600, cursor: page >= totalPages - 1 ? 'default' : 'pointer', opacity: page >= totalPages - 1 ? 0.4 : 1 }}>
            {lang === 'ar' ? 'التالي' : 'Next'}
          </button>
        </div>
      )}
    </div>
  );
}

export default function LeadAnalytics() {
  const { t, lang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const navigate = useNavigate();
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [touchDrill, setTouchDrill] = useState(null); // { name, bucket }
  const [onceDroppedDrill, setOnceDroppedDrill] = useState(null); // member name
  // Click-to-sort for the per-rep communication-activity table.
  const [perfSort, setPerfSort] = useState({ key: 'silent', dir: 'desc' });
  const togglePerfSort = (key) => setPerfSort((p) => p.key === key
    ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: key === 'name' ? 'asc' : 'desc' });

  // Open the full per-rep analytics page, carrying the current period + range.
  const goMember = (name) => {
    const { from, to } = presetRange(period, customFrom, customTo);
    const qs = from && to
      ? `?period=${encodeURIComponent(period)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
      : `?period=${encodeURIComponent(period)}`;
    navigate(`/analytics/member/${encodeURIComponent(name)}${qs}`);
  };

  useEffect(() => {
    const { from, to } = presetRange(period, customFrom, customTo);
    // Custom range needs both ends before it can fetch.
    if (period === 'custom' && (!from || !to)) { setLoading(false); return; }
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get('/api/leads/analytics', { params: { date_from: from, date_to: to } });
        setData(res.data);
      } catch {}
      setLoading(false);
    })();
  }, [period, customFrom, customTo]);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 80 }}>
      <div style={{ fontSize: '2rem', marginBottom: 12, opacity: 0.3 }}>&#9203;</div>
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{t('loading')}</div>
    </div>
  );
  if (!data) return (
    <div style={{ textAlign: 'center', padding: 80 }}>
      <div style={{ fontSize: '2rem', marginBottom: 12, opacity: 0.3 }}>&#9888;</div>
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Failed to load analytics</div>
    </div>
  );

  const { salesPerformance, funnel, bySource, weeklyActivity, overall, visitScheduledCount, visitLostCount, staleCount, deals, lossReasonsSummary, lossBySource } = data;
  const fmtMoney = n => {
    const v = Number(n) || 0;
    if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M';
    if (v >= 1_000) return (v / 1_000).toFixed(0) + 'K';
    return v.toFixed(0);
  };
  const periodLabel = Object.fromEntries(PRESET_KEYS.map(k => [k, t(PRESET_LABEL_KEY[k])]));

  const totalWorked = salesPerformance.reduce((s, m) => s + (m.todayWorked || 0), 0);
  // Team-wide "never communicated" total (assigned, still-open, zero comms) —
  // summed from the per-rep silent-leads metric. Drillable via the KPI card.
  const neverContactedCount = salesPerformance.reduce((s, m) => s + (m.comm?.silentLeads || 0), 0);

  // Cumulative funnel — "leads that ever reached each stage" (server-computed,
  // strictly monotonic). Drop stages no one reached so step-conversion stays
  // meaningful. Each row carries its share of the top stage and the step-over-step
  // conversion from the stage above it.
  const cumulativeFunnel = (() => {
    const stages = (funnel || []).filter(s => s.count > 0);
    const top = stages[0]?.count || 0;
    return stages.map((s, i) => ({
      status: s.status,
      name: t('crmStatus_' + s.status),
      count: s.count,
      pctFromTop: top > 0 ? (s.count / top * 100) : 0,
      stepPct: i === 0 ? 100 : (stages[i - 1].count > 0 ? (s.count / stages[i - 1].count * 100) : 0),
      dropoff: i === 0 ? 0 : Math.max(0, stages[i - 1].count - s.count),
    }));
  })();
  const cumOverallPct = cumulativeFunnel.length > 1
    ? (cumulativeFunnel[cumulativeFunnel.length - 1].count / cumulativeFunnel[0].count * 100) : 0;

  // Source win-rate data — only sources with at least 1 lead
  const sourceWinData = bySource
    .filter(s => s.total > 0)
    .map(s => ({
      source: sourceLabel(s.source, lang),
      total: s.total,
      won: s.won,
      winRate: s.total > 0 ? (s.won / s.total * 100) : 0,
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 8);

  // Team-wide follow-up effort vs outcome: touch buckets (1x..5+x) split by won/open/lost
  const effortOutcomeData = (() => {
    const agg = {}; [1, 2, 3, 4, 5].forEach(b => { agg[b] = { won: 0, open: 0, lost: 0 }; });
    salesPerformance.forEach(s => (s.effort?.distribution || []).forEach(d => {
      const b = d.bucket >= 5 ? 5 : d.bucket; if (!agg[b]) return;
      agg[b].won += d.won || 0; agg[b].open += d.open || 0; agg[b].lost += d.lost || 0;
    }));
    return [1, 2, 3, 4, 5].map(b => {
      const o = agg[b]; const total = o.won + o.open + o.lost;
      return { name: b >= 5 ? '5+\u00D7' : b + '\u00D7', won: o.won, open: o.open, lost: o.lost, total };
    }).filter(d => d.total > 0);
  })();

  return (
    <div>
      {/* ── Period Filter ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: '1.3rem', fontWeight: 800, margin: 0 }}>{t('anSalesPerformance')}</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="tabs" style={{ background: 'var(--bg-card)', borderRadius: 10, padding: 3 }}>
            {PRESET_KEYS.map(p => (
              <button key={p} className={`tab ${period === p ? 'active' : ''}`} onClick={() => setPeriod(p)}>
                {periodLabel[p]}
              </button>
            ))}
          </div>
          {period === 'custom' && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="date" value={customFrom} max={customTo || ksaDate(0)} onChange={e => setCustomFrom(e.target.value)} style={{ padding: '5px 8px', fontSize: '0.8rem', borderRadius: 8, border: '1px solid var(--border)' }} />
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>—</span>
              <input type="date" value={customTo} min={customFrom || undefined} max={ksaDate(0)} onChange={e => setCustomTo(e.target.value)} style={{ padding: '5px 8px', fontSize: '0.8rem', borderRadius: 8, border: '1px solid var(--border)' }} />
            </div>
          )}
        </div>
      </div>

      {/* ── Hero KPI Row — trimmed to 6 high-signal metrics ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { value: overall.total, label: t('crmTotalLeads'), color: 'var(--text-primary)', icon: '\uD83D\uDCC8', type: 'total', hint: t('hintTotalLeads'), clickable: true },
          { value: totalWorked, label: periodLabel[period] + ' — ' + t('anWorkedToday'), color: 'var(--accent)', icon: '\u2705', type: 'worked', hint: t('hintWorked'), clickable: true },
          { value: visitScheduledCount || 0, label: t('anVisitScheduled'), color: '#f59e0b', icon: '\uD83D\uDCC5', type: 'visit_scheduled', hint: t('hintVisitScheduled'), clickable: true },
          { value: visitLostCount || 0, label: t('anVisitLost'), color: '#ef4444', icon: '\uD83D\uDEAB', type: 'visit_lost', hint: t('hintVisitLost'), clickable: true },
          { value: overall.won, label: t('crmWonDeals'), color: '#22c55e', icon: '\uD83C\uDFC6', type: 'won', hint: t('hintWon'), clickable: true },
          { value: staleCount || 0, label: t('anStaleLeads'), color: '#dc2626', icon: '\u26A0', type: 'stale', hint: t('hintStale'), clickable: true },
          { value: neverContactedCount || 0, label: t('anNeverContacted'), color: '#dc2626', icon: '\uD83D\uDD15', type: 'never_contacted', hint: t('hintNeverContacted'), clickable: true },
          { value: fmtMoney(deals?.teamComm || 0), label: t('anCommission'), color: '#8b5cf6', icon: '\uD83D\uDCB0', type: 'commission', hint: t('hintCommission'), clickable: false, sub: deals?.count ? `${deals.count} ${t('crmWonDeals')} \u00B7 ${fmtMoney(deals.revenue)} ${t('anRevenue')}` : null },
        ].map((kpi, i) => (
          <div key={i} onClick={() => kpi.type === 'stale' ? navigate('/stale-leads') : (kpi.clickable && setSelectedKpi(kpi))} style={{
            background: 'var(--bg-card)', borderRadius: 14, padding: '16px 14px',
            border: '1px solid var(--border)', textAlign: 'center',
            transition: 'transform 0.15s, box-shadow 0.15s', cursor: kpi.clickable ? 'pointer' : 'default',
          }}
            onMouseEnter={e => { if (kpi.clickable) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; } }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{ fontSize: '0.85rem', marginBottom: 6, opacity: 0.7 }}>{kpi.icon}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: kpi.color, lineHeight: 1 }}>{kpi.value}</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 6, fontWeight: 500 }}>{kpi.label}</div>
            {kpi.sub && <div style={{ fontSize: '0.62rem', color: 'var(--text-secondary)', marginTop: 3, opacity: 0.75, fontWeight: 600 }}>{kpi.sub}</div>}
            <div style={{ fontSize: '0.58rem', color: 'var(--text-secondary)', marginTop: 4, opacity: 0.6, lineHeight: 1.3 }}>{kpi.hint}</div>
          </div>
        ))}
      </div>

      {/* ── Team Today Board — outstanding work first ── */}
      <TodayBoard t={t} />

      {/* ── Team Performance Cards ── */}
      <div className="card mb-6" style={{ overflow: 'visible' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>{t('anDailyPerformance')}</h2>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 400 }}>{t('anTodayDetail')}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, padding: 16 }}>
          {salesPerformance.map((s, i) => {
            const total = s.todayTotalTasks || 0;
            const done = s.todayWorked || 0;
            const pct = s.todayCompletionRate || 0;
            const kpi = s.dailyKpi || 0;
            const target = s.periodTarget || 0;
            const remaining = Math.max(0, (kpi > 0 ? target : total) - done);
            const barColor = pct >= 80 ? '#22c55e' : pct >= 50 ? '#f59e0b' : pct > 0 ? '#ef4444' : 'var(--border)';
            const section = (label) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '14px 0 8px' }}>
                <span style={{ fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{label}</span>
                <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              </div>
            );
            return (
              <div key={i} onClick={() => goMember(s.name)} style={{
                padding: '18px 20px', background: 'var(--bg-primary)', borderRadius: 14,
                border: '1px solid var(--border)', cursor: 'pointer', transition: 'all 0.2s',
                position: 'relative', overflow: 'hidden',
              }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
              >
                {/* Subtle gradient accent */}
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${barColor}, ${barColor}88)`, borderRadius: '14px 14px 0 0' }} />

                {/* Header: name + total + completion ring */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '1rem' }}>{s.name}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 3 }}>{s.total} {t('crmTotalLeads')}</div>
                  </div>
                  <div style={{
                    width: 50, height: 50, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: barColor + '12', border: `2.5px solid ${barColor}`, flexShrink: 0,
                    fontWeight: 800, fontSize: '0.9rem', color: barColor,
                  }}>
                    {pct.toFixed(0)}%
                  </div>
                </div>

                {/* Per-member breakdown for the selected period — a true partition that
                    sums to total: new + follow-up + visits + won + lost = leads */}
                {(() => {
                  const ps = s.periodStats || { total: 0, new: 0, followup: 0, visits: 0, won: 0, lost: 0 };
                  const cells = [
                    { v: ps.new, label: lang === 'ar' ? 'جديد' : 'New', color: '#8b5cf6' },
                    { v: ps.followup, label: lang === 'ar' ? 'متابعات' : 'Follow-up', color: '#f59e0b' },
                    { v: ps.visits, label: lang === 'ar' ? 'زيارات' : 'Visits', color: '#06b6d4' },
                    { v: ps.won, label: lang === 'ar' ? 'صفقات' : 'Won', color: '#22c55e' },
                    { v: ps.lost, label: lang === 'ar' ? 'خسارة' : 'Lost', color: '#ef4444' },
                  ];
                  return (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 6 }}>
                        {lang === 'ar' ? 'للفترة المحددة' : 'For selected period'} · <span style={{ color: 'var(--accent)' }}>{ps.total} {lang === 'ar' ? 'عميل' : 'leads'}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, textAlign: 'center' }}>
                        {cells.map((c, j) => (
                          <div key={j} style={{ padding: '8px 2px', borderRadius: 8, background: c.color + '12' }}>
                            <div style={{ fontWeight: 800, fontSize: '1.05rem', color: c.color }}>{c.v}</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.54rem', marginTop: 2, lineHeight: 1.2 }}>{c.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Progress bar */}
                <div style={{ height: 6, borderRadius: 3, background: 'var(--border)', marginBottom: 12, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: Math.min(pct, 100) + '%', borderRadius: 3, background: `linear-gradient(90deg, ${barColor}, ${barColor}cc)`, transition: 'width 0.6s ease' }} />
                </div>

                {/* ZONE 1 — TODAY: target / done / remaining (reconciles with %) */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, textAlign: 'center' }}>
                  <div style={{ padding: '8px 0', borderRadius: 8, background: 'rgba(99,102,241,0.06)' }}>
                    <div style={{ fontWeight: 800, fontSize: '1.15rem', color: 'var(--accent)' }}>{kpi > 0 ? target : total}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.62rem', marginTop: 2 }}>{kpi > 0 ? t('anTargetLabel') : t('crmTodayActual')}</div>
                  </div>
                  <div style={{ padding: '8px 0', borderRadius: 8, background: 'rgba(34,197,94,0.06)' }}>
                    <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#22c55e' }}>{done}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.62rem', marginTop: 2 }}>{t('anCompleted')}</div>
                  </div>
                  <div style={{ padding: '8px 0', borderRadius: 8, background: remaining > 0 ? 'rgba(239,68,68,0.06)' : 'rgba(136,136,136,0.04)' }}>
                    <div style={{ fontWeight: 800, fontSize: '1.15rem', color: remaining > 0 ? '#ef4444' : 'var(--text-secondary)' }}>{remaining}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.62rem', marginTop: 2 }}>{t('anRemaining')}</div>
                  </div>
                </div>

                {/* Actionable today — overdue / not-contacted / scheduled visits */}
                {((s.backlog && (s.backlog.notContacted > 0 || s.backlog.overdueFollowups > 0 || s.backlog.overdueVisits > 0)) || s.todayScheduledVisits > 0) && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    {((s.backlog?.overdueFollowups || 0) + (s.backlog?.overdueVisits || 0)) > 0 && (
                      <div onClick={e => { e.stopPropagation(); goMember(s.name); }} title={`${s.backlog?.overdueFollowups || 0} ${t('crmTodayOverdue')} + ${s.backlog?.overdueVisits || 0} ${t('crmOverdueVisitsTitle')}`} style={{ flex: 1, textAlign: 'center', padding: '5px 4px', borderRadius: 6, background: '#ef444410', cursor: 'pointer' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#ef4444' }}>{(s.backlog?.overdueFollowups || 0) + (s.backlog?.overdueVisits || 0)}</span>
                        <span style={{ fontSize: '0.55rem', color: 'var(--text-secondary)', marginInlineStart: 4 }}>{t('crmTodayOverdue')}</span>
                      </div>
                    )}
                    {s.backlog?.notContacted > 0 && (
                      <div onClick={e => { e.stopPropagation(); goMember(s.name); }} style={{ flex: 1, textAlign: 'center', padding: '5px 4px', borderRadius: 6, background: '#dc262610', cursor: 'pointer' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#dc2626' }}>{s.backlog.notContacted}</span>
                        <span style={{ fontSize: '0.55rem', color: 'var(--text-secondary)', marginInlineStart: 4 }}>{t('crmTodayBehind')}</span>
                      </div>
                    )}
                    {s.todayScheduledVisits > 0 && (
                      <div style={{ flex: 1, textAlign: 'center', padding: '5px 4px', borderRadius: 6, background: '#06b6d410' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#06b6d4' }}>{s.todayScheduledVisits}</span>
                        <span style={{ fontSize: '0.55rem', color: 'var(--text-secondary)', marginInlineStart: 4 }}>{t('crmTodayVisits')}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* TODAY'S RESULTS — what the leads worked today turned into */}
                {(() => {
                  const items = [
                    { label: t('crmStatus_interested'), value: s.todayInterested || 0, color: '#22c55e' },
                    { label: t('crmStatus_site_visit_scheduled'), value: s.todayVisit || 0, color: '#06b6d4' },
                    { label: t('crmStatus_offer_sent'), value: s.todayOffer || 0, color: '#0ea5e9' },
                    { label: t('crmStatus_contacted'), value: s.todayContacted || 0, color: '#94a3b8' },
                    { label: t('crmOutcome_not_answering'), value: s.todayNoAnswer || 0, color: '#f59e0b' },
                  ].filter(x => x.value > 0);
                  const sum = items.reduce((a, x) => a + x.value, 0);
                  return (
                    <>
                      {section(`${t('anTodayResults')}${s.todayWorked > 0 ? `  \u00B7  ${s.todayWorked}` : ''}`)}
                      {sum === 0 ? (
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', padding: '2px 0 4px' }}>{t('anNoWorkToday')}</div>
                      ) : (
                        <>
                          <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', marginBottom: 8 }}>
                            {items.map((x, j) => (
                              <div key={j} title={`${x.label}: ${x.value}`} style={{ width: (x.value / sum * 100) + '%', background: x.color }} />
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {items.map((x, j) => (
                              <span key={j} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: x.color }} />
                                {x.label}<b style={{ color: x.color, fontWeight: 800 }}>{x.value}</b>
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}

                {/* ZONE 2 — FOLLOW-UP QUALITY: avg touches, once-dropped, no-answer + distribution */}
                {s.effort && (() => {
                  const avg = Number(s.effort.avgTouches) || 0;
                  const dropped = Number(s.effort.onceDropped) || 0;
                  const droppedPct = s.total > 0 ? Math.round((dropped / s.total) * 100) : 0;
                  const avgColor = avg >= 3 ? '#22c55e' : avg >= 2 ? '#f59e0b' : '#ef4444';
                  const distMap = {};
                  (s.effort.distribution || []).forEach(d => { distMap[d.bucket] = d; });
                  const contacted = Number(s.effort.contacted) || 0;
                  const bucketColor = b => b === 1 ? '#ef4444' : b === 2 ? '#f59e0b' : b === 3 ? '#3b82f6' : '#22c55e';
                  const bucketLabel = b => b >= 5 ? '5+\u00D7' : b + '\u00D7';
                  return (
                    <>
                      {section(t('anFollowupQuality'))}
                      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <div style={{ flex: 1, textAlign: 'center', padding: '7px 4px', borderRadius: 8, background: avgColor + '12' }}>
                          <div style={{ fontWeight: 800, fontSize: '1.05rem', color: avgColor }}>{avg.toFixed(1)}{'\u00D7'}</div>
                          <div style={{ fontSize: '0.58rem', color: 'var(--text-secondary)', marginTop: 2 }}>{t('anEffortAvgTouches')}</div>
                        </div>
                        <div onClick={dropped > 0 ? (ev => { ev.stopPropagation(); setOnceDroppedDrill(s.name); }) : undefined} style={{ flex: 1, textAlign: 'center', padding: '7px 4px', borderRadius: 8, background: dropped > 0 ? 'rgba(239,68,68,0.08)' : 'rgba(136,136,136,0.04)', cursor: dropped > 0 ? 'pointer' : 'default' }}>
                          <div style={{ fontWeight: 800, fontSize: '1.05rem', color: dropped > 0 ? '#ef4444' : 'var(--text-secondary)' }}>{dropped}{dropped > 0 ? ` · ${droppedPct}%` : ''}</div>
                          <div style={{ fontSize: '0.58rem', color: 'var(--text-secondary)', marginTop: 2 }}>{t('anEffortOnceDropped')}{dropped > 0 ? ' ›' : ''}</div>
                        </div>
                        {(s.noAnswer || 0) > 0 && (
                          <div style={{ flex: 1, textAlign: 'center', padding: '7px 4px', borderRadius: 8, background: '#f59e0b12' }}>
                            <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#f59e0b' }}>{s.noAnswer}</div>
                            <div style={{ fontSize: '0.58rem', color: 'var(--text-secondary)', marginTop: 2 }}>{t('crmOutcome_not_answering')}</div>
                          </div>
                        )}
                      </div>
                      {contacted > 0 && (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 4 }}>
                            {[1, 2, 3, 4, 5].map(b => {
                              const d = distMap[b] || { count: 0, won: 0, lost: 0, open: 0 };
                              const cnt = d.count || 0;
                              const won = d.won || 0, lost = d.lost || 0, open = d.open || 0;
                              return (
                                <div key={b} title={`${bucketLabel(b)}: ${cnt} (${Math.round(cnt / contacted * 100)}%) — ${t('crmStatus_won')} ${won} · ${t('crmStatus_lost')} ${lost} · ${t('anStillOpen')} ${open}`}
                                  onClick={cnt > 0 ? (ev => { ev.stopPropagation(); setTouchDrill({ name: s.name, bucket: b }); }) : undefined}
                                  style={{ textAlign: 'center', padding: '5px 2px 4px', borderRadius: 6, background: cnt > 0 ? bucketColor(b) + '14' : 'rgba(136,136,136,0.04)', cursor: cnt > 0 ? 'pointer' : 'default' }}>
                                  <div style={{ fontWeight: 800, fontSize: '0.82rem', color: cnt > 0 ? bucketColor(b) : 'var(--text-secondary)' }}>{cnt}</div>
                                  <div style={{ fontSize: '0.55rem', color: 'var(--text-secondary)', marginTop: 1, marginBottom: 3 }}>{bucketLabel(b)}</div>
                                  {cnt > 0 && (
                                    <>
                                      <div style={{ display: 'flex', height: 4, borderRadius: 2, overflow: 'hidden', background: 'rgba(136,136,136,0.15)' }}>
                                        {won > 0 && <div style={{ width: (won / cnt * 100) + '%', background: '#22c55e' }} />}
                                        {open > 0 && <div style={{ width: (open / cnt * 100) + '%', background: '#3b82f6' }} />}
                                        {lost > 0 && <div style={{ width: (lost / cnt * 100) + '%', background: '#ef4444' }} />}
                                      </div>
                                      <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 3, fontSize: '0.56rem', fontWeight: 700 }}>
                                        <span style={{ color: '#22c55e' }}>{won}</span>
                                        <span style={{ color: '#3b82f6' }}>{open}</span>
                                        <span style={{ color: '#ef4444' }}>{lost}</span>
                                      </div>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <div style={{ display: 'flex', gap: 12, marginTop: 5, justifyContent: 'center' }}>
                            {[{ c: '#22c55e', l: t('crmStatus_won') }, { c: '#3b82f6', l: t('anStillOpen') }, { c: '#ef4444', l: t('crmStatus_lost') }].map((x, j) => (
                              <span key={j} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.55rem', color: 'var(--text-secondary)' }}>
                                <span style={{ width: 7, height: 7, borderRadius: 2, background: x.c }} />{x.l}
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}

                {/* ZONE 3 — PIPELINE: portfolio current-state funnel (single source, no duplicates) */}
                {(() => {
                  const pipeline = [
                    { label: t('crmStatus_contacted'), value: s.contacted || 0, color: '#3b82f6', status: 'contacted' },
                    { label: t('crmStatus_interested'), value: s.interested || 0, color: '#8b5cf6', status: 'interested' },
                    { label: t('crmStatus_site_visit_scheduled'), value: (s.visitScheduled || 0) + (s.visitDone || 0), color: '#06b6d4', status: 'site_visit_scheduled,site_visit_done' },
                    { label: t('crmStatus_offer_sent'), value: s.offerSent || 0, color: '#0ea5e9', status: 'offer_sent' },
                    { label: t('crmStatus_negotiating'), value: s.negotiating || 0, color: '#ec4899', status: 'negotiating' },
                    { label: t('crmStatus_won'), value: s.won || 0, color: '#22c55e', status: 'won' },
                  ].filter(p => p.value > 0);
                  if (pipeline.length === 0) return null;
                  return (
                    <>
                      {section(t('anPipeline'))}
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {pipeline.map((p, j) => (
                          <span key={j} title={lang === 'ar' ? 'عملاء مُستلَمون في الفترة بهذه الحالة — اضغط للقائمة' : 'Leads received in the period with this status — click for the list'}
                            onClick={e => { e.stopPropagation(); setSelectedKpi({ type: 'member_status', label: `${p.label} — ${s.name}`, color: p.color, member: s.name, status: p.status }); }}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 8, fontSize: '0.68rem', fontWeight: 600, background: p.color + '14', color: p.color, cursor: 'pointer' }}>
                            {p.label}<b style={{ fontWeight: 800 }}>{p.value}</b>
                          </span>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Cumulative Conversion Funnel — "ever reached each stage" + step conversion ── */}
      {cumulativeFunnel.length > 0 && (
        <div className="card mb-6">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <h2>{t('anFunnelCumulative')}</h2>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 400 }}>{t('anFunnelCumulativeHint')}</span>
          </div>
          <div style={{ padding: 16 }}>
            {/* Overall conversion summary */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1.9rem', fontWeight: 800, color: '#22c55e', lineHeight: 1 }}>{cumOverallPct.toFixed(1)}%</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                {t('anConversionOverall')}  {'·'}  {cumulativeFunnel[0].count} {t('crmStatus_new')} {'→'} {cumulativeFunnel[cumulativeFunnel.length - 1].count} {t('crmStatus_won')}
              </span>
            </div>
            {/* Funnel rows */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {cumulativeFunnel.map((s, i) => {
                const color = STATUS_COLORS[s.status] || '#6366f1';
                const stepColor = s.stepPct >= 50 ? '#22c55e' : s.stepPct >= 25 ? '#f59e0b' : '#ef4444';
                return (
                  <div key={s.status}>
                    {/* Step-conversion connector between stages */}
                    {i > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', marginInlineStart: 140 }}>
                        <span style={{ fontSize: '0.62rem', fontWeight: 700, color: stepColor }}>
                          {'↓'} {s.stepPct.toFixed(0)}% {t('anConversionStep')}
                        </span>
                        {s.dropoff > 0 && (
                          <span style={{ fontSize: '0.6rem', color: 'var(--text-secondary)' }}>{'−'}{s.dropoff}</span>
                        )}
                      </div>
                    )}
                    {/* Stage bar */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 130, fontSize: '0.74rem', fontWeight: 600, textAlign: 'end', flexShrink: 0, color: 'var(--text-primary)' }}>{s.name}</div>
                      <div style={{ flex: 1, height: 26, background: 'var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: Math.max(s.pctFromTop, 3) + '%', background: `linear-gradient(90deg, ${color}, ${color}cc)`, borderRadius: 6, transition: 'width 0.6s ease', display: 'flex', alignItems: 'center', paddingInlineStart: 8 }}>
                          <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#fff' }}>{s.count}</span>
                        </div>
                      </div>
                      <div style={{ width: 48, fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', flexShrink: 0 }}>{s.pctFromTop.toFixed(0)}%</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: '0.62rem', color: 'var(--text-secondary)', marginTop: 12, opacity: 0.7 }}>{t('anConversionFromTop')}: %</div>
          </div>
        </div>
      )}

      {/* ── Number of Calls & Their Outcomes (team-wide) — under Daily Performance ── */}
      {effortOutcomeData.length > 0 && (
        <div className="card mb-6">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <h2>{t('anEffortOutcome')}</h2>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 400 }}>{t('anEffortOutcomeHint')}</span>
          </div>
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={effortOutcomeData} margin={{ left: 0, right: 10, top: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="name" stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
                <YAxis stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
                <Tooltip contentStyle={tooltipStyle} formatter={(val, name, p) => [`${val} (${Math.round(val / (p.payload.total || 1) * 100)}%)`, name]} />
                <Legend wrapperStyle={{ fontSize: '0.72rem' }} />
                <Bar dataKey="won" stackId="a" fill="#22c55e" name={t('crmStatus_won')} radius={[0, 0, 0, 0]} />
                <Bar dataKey="open" stackId="a" fill="#3b82f6" name={t('anStillOpen')} radius={[0, 0, 0, 0]} />
                <Bar dataKey="lost" stackId="a" fill="#ef4444" name={t('crmStatus_lost')} radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="total" position="top" style={{ fill: 'var(--text-primary)', fontSize: 11, fontWeight: 700 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Loss Reasons — directly under the calls-outcomes chart ── */}
      <LossReasonsCard lossReasonsSummary={lossReasonsSummary} lossBySource={lossBySource} period={period} customFrom={customFrom} customTo={customTo} t={t} lang={lang} />

      <TransitionExplorer period={period} customFrom={customFrom} customTo={customTo} reps={salesPerformance.map(s => s.name)} t={t} lang={lang} />

      {/* ── Communication activity per rep ── */}
      {salesPerformance.length > 0 && (
        <div className="card mb-6">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <h2>{t('anCommStatsTitle')}</h2>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 400 }}>{t('anCommStatsHint')}</span>
          </div>
          <div className="data-table-wrapper">
            <table className="data-table" style={{ fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  {[
                    { key: 'name', label: t('crmAssignedTo') },
                    { key: 'logged', label: t('anCommsLogged') },
                    { key: 'touched', label: t('anCommsLeadsTouched') },
                    { key: 'silent', label: t('anSilentLeads'), color: '#ef4444' },
                    { key: 'win', label: t('anAvgToWin'), color: '#22c55e' },
                    { key: 'loss', label: t('anAvgToLoss'), color: '#ef4444' },
                  ].map((col) => (
                    <th key={col.key} onClick={() => togglePerfSort(col.key)} title={lang === 'ar' ? 'اضغط للترتيب' : 'Click to sort'}
                      style={{ color: col.color, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                      {col.label}{perfSort.key === col.key ? (perfSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const val = (s, key) => {
                    const c = s.comm || {};
                    if (key === 'name') return (s.name || '').toLowerCase();
                    if (key === 'logged') return c.logged || 0;
                    if (key === 'touched') return c.leadsTouched || 0;
                    if (key === 'silent') return c.silentLeads || 0;
                    if (key === 'win') return c.avgTouchesToWin != null ? c.avgTouchesToWin : -1;
                    if (key === 'loss') return c.avgTouchesToLoss != null ? c.avgTouchesToLoss : -1;
                    return 0;
                  };
                  const dir = perfSort.dir === 'asc' ? 1 : -1;
                  return [...salesPerformance].sort((a, b) => {
                    const av = val(a, perfSort.key), bv = val(b, perfSort.key);
                    return av < bv ? -dir : av > bv ? dir : 0;
                  });
                })().map((s, i) => {
                  const c = s.comm || {};
                  return (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{s.name}</td>
                      <td style={{ fontWeight: 700 }}>{c.logged || 0}</td>
                      <td>{c.leadsTouched || 0}</td>
                      <td>
                        {(c.silentLeads || 0) > 0 ? (
                          <span onClick={() => setSelectedKpi({ type: 'never_contacted', label: `${t('anNeverContacted')} — ${s.name}`, color: '#ef4444', member: s.name })}
                            title={t('hintNeverContacted')}
                            style={{ padding: '2px 8px', borderRadius: 8, fontSize: '0.72rem', fontWeight: 700, background: '#ef444418', color: '#ef4444', cursor: 'pointer' }}>{c.silentLeads}</span>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }}>0</span>
                        )}
                      </td>
                      <td style={{ color: c.avgTouchesToWin != null ? '#22c55e' : 'var(--text-secondary)', fontWeight: 700 }}>
                        {c.avgTouchesToWin != null ? c.avgTouchesToWin.toFixed(1) : '\u2014'}
                      </td>
                      <td style={{ color: c.avgTouchesToLoss != null ? '#ef4444' : 'var(--text-secondary)', fontWeight: 700 }}>
                        {c.avgTouchesToLoss != null ? c.avgTouchesToLoss.toFixed(1) : '\u2014'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}


      {/* ── Team Comparison (period-based) ── */}
      {salesPerformance.length > 0 && salesPerformance.some(s => s.todayWorked > 0) && (
        <div className="card mb-6">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>{t('anTeamComparison')} — {periodLabel[period]}</h2>
          </div>
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={salesPerformance.filter(s => s.todayWorked > 0)} margin={{ left: 0, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="name" stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
                <YAxis stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: '0.72rem' }} />
                <Bar dataKey="todayWorked" fill="var(--accent)" name={t('anWorkedToday')} radius={[4, 4, 0, 0]} />
                <Bar dataKey="todayContacted" fill="#3b82f6" name={t('anSetContacted')} radius={[4, 4, 0, 0]} />
                <Bar dataKey="todayInterested" fill="#8b5cf6" name={t('anSetInterested')} radius={[4, 4, 0, 0]} />
                <Bar dataKey="todayVisit" fill="#06b6d4" name={t('anSetVisit')} radius={[4, 4, 0, 0]} />
                <Bar dataKey="todayLost" fill="#ef4444" name={t('anSetLost')} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Win-rate by source ── */}
      <div className="card mb-6">
          <div className="card-header">
            <h2>{t('anWinRate')}</h2>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 400 }}>{t('crmWonDeals')} / {t('crmTotalLeads')}</span>
          </div>
          <div style={{ height: 300 }}>
            {sourceWinData.length === 0 ? (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{t('noData')}</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sourceWinData} margin={{ left: 0, right: 10, top: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="source" stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 9 }} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis yAxisId="left" stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
                  <YAxis yAxisId="right" orientation="right" stroke="#22c55e" tick={{ fill: '#22c55e', fontSize: 10 }} unit="%" />
                  <Tooltip contentStyle={tooltipStyle} formatter={(val, name) => name === t('anWinRate') ? [val.toFixed(1) + '%', name] : [val, name]} />
                  <Legend wrapperStyle={{ fontSize: '0.72rem' }} />
                  <Bar yAxisId="left" dataKey="total" fill="var(--accent)" name={t('crmTotalLeads')} radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="winRate" fill="#22c55e" name={t('anWinRate')} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

      {/* ── Weekly Activity ── */}
      {weeklyActivity && weeklyActivity.length > 0 && (
        <div className="card mb-6">
          <div className="card-header"><h2>{t('anWeeklyActivity')}</h2></div>
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weeklyActivity} margin={{ left: 0, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
                <YAxis stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: '0.72rem' }} />
                {salesPerformance.map((s, i) => {
                  const colors = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899'];
                  return <Line key={s.name} type="monotone" dataKey={s.name} stroke={colors[i % colors.length]} strokeWidth={2.5} dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} name={s.name} />;
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── KPI Detail Modal ── */}
      {selectedKpi && (
        <KpiDetailModal type={selectedKpi.type} title={selectedKpi.label} color={selectedKpi.color} period={period} customFrom={customFrom} customTo={customTo} member={selectedKpi.member} status={selectedKpi.status} onClose={() => setSelectedKpi(null)} t={t} />
      )}

      {/* ── Touch-bucket Leads Modal ── */}
      {touchDrill && (
        <TouchLeadsModal name={touchDrill.name} bucket={touchDrill.bucket} onClose={() => setTouchDrill(null)} t={t} />
      )}
      {onceDroppedDrill && (
        <TouchLeadsModal endpoint={`/api/leads/once-dropped-leads?member=${encodeURIComponent(onceDroppedDrill)}`} title={onceDroppedDrill} subtitle={t('anEffortOnceDropped')} onClose={() => setOnceDroppedDrill(null)} t={t} />
      )}
    </div>
  );
}
